import React, { memo } from "react";
import salesicon from "@/assets/images/salesicon.png";
import animatedLogo from "@/assets/images/animated_logo.gif";
import CommonAvatar from "../Avatar/CommonAvatar";
import MessageContent from "./MessageContent";
import ReasoningSteps from "./ReasoningSteps";
import FollowUpOptions from "./FollowUpOptions";
import Loader from "./Loader";
import EmailDraftCard from "./EmailDraftCard";

/**
 * prompt-kit style chat message: user bubble on the right, assistant markdown
 * on the left with optional reasoning steps and follow-up option chips.
 */
const Message = memo(
  ({
    message,
    isStreaming = false,
    userImage,
    userName,
    onOptionSelect,
    showFollowUp = true,
    disabled = false,
  }) => {
    const isUser = message.role === "user";
    const stopped = Boolean(message.agent_result?.stopped);
    const allSteps =
      message.progress_steps?.length
        ? message.progress_steps
        : message.agent_result?.progress_steps?.length
        ? message.agent_result.progress_steps
        : message.reasoning_steps?.length
        ? message.reasoning_steps
        : message.steps || [];
    const reasoningSteps = allSteps;
    const followUp = message.follow_up;
    const shouldShowFollowUp =
      showFollowUp && Array.isArray(followUp?.options) && followUp.options.length > 0;
    const visibleContent =
      shouldShowFollowUp &&
      message.content?.trim() === followUp?.question?.trim()
        ? ""
        : message.content;

    if (isUser) {
      return (
        <div className="relative ml-auto flex w-fit max-w-[85%] text-sm text-gray-800 dark:text-gray-100">
          <CommonAvatar
            imageUrl={userImage}
            name={userName}
            className="absolute right-0 top-0 h-8 w-8 flex-shrink-0"
            size="md"
          />
          <div
            style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            className="ml-auto mr-12 w-full overflow-hidden rounded-md bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3 text-sm text-gray-800 dark:text-gray-100"
          >
            {message.content}
          </div>
        </div>
      );
    }

    return (
      <div className="relative flex min-h-8 w-full items-start text-sm text-gray-800 dark:text-gray-100 mb-1">
        <img
          src={isStreaming ? animatedLogo : salesicon}
          alt="assistant"
          className="absolute top-0 h-8 w-8 flex-shrink-0"
        />
        <div className="w-full ml-12 min-w-0">
          <ReasoningSteps
            steps={reasoningSteps}
            // A stopped reply keeps its trace open: it is the whole answer.
            defaultOpen={isStreaming || stopped}
            live={isStreaming}
          />
          {visibleContent ? (
            <div
              className="markdown-content w-full overflow-hidden break-words rounded-md bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3 text-sm text-gray-800 dark:text-gray-100"
              style={{ wordBreak: "break-word" }}
            >
              <MessageContent
                markdown
                id={message.id}
                className="prose prose-sm dark:prose-invert max-w-none prose-h2:mt-0 prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 text-gray-800 dark:text-gray-100"
              >
                {visibleContent}
              </MessageContent>
            </div>
          ) : isStreaming && !reasoningSteps.length ? (
            <div className="py-1">
              <Loader label="Thinking" />
            </div>
          ) : null}
          {stopped && (
            <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
              You stopped this response.
            </p>
          )}
          <EmailDraftCard draft={message.agent_result?.email_draft} />
          {shouldShowFollowUp && (
            <FollowUpOptions
              followUp={followUp}
              onOptionSelect={onOptionSelect}
              disabled={disabled}
            />
          )}
        </div>
      </div>
    );
  },
);

export default Message;
